interface StoredPendingReview {
  publishAt: string;
  savedAt: string;
  professor: string;
  quarter: string;
  median?: "A" | "A-" | "B+" | "B" | "B-" | "C+" | "Unsure" | null;
  comments: string;
}

export interface PendingReviewState extends StoredPendingReview {}

const STALE_BUFFER_MS = 24 * 60 * 60 * 1000;

function canUseStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function storageKey(userId: string, courseId: string): string {
  return `layup:review:pending:${userId}:${courseId}`;
}

function parseStoredValue<T>(rawValue: string | null): T | null {
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return null;
  }
}

function isPendingStillRelevant(
  publishAt: string,
  savedAt: string,
  snapshotGeneratedAt?: string,
): boolean {
  const publishTime = new Date(publishAt).getTime();
  if (!Number.isFinite(publishTime)) {
    return false;
  }

  if (snapshotGeneratedAt) {
    const snapshotTime = new Date(snapshotGeneratedAt).getTime();
    if (Number.isFinite(snapshotTime)) {
      const savedTime = new Date(savedAt).getTime();
      if (snapshotTime >= publishTime) {
        return false;
      }
      if (Number.isFinite(savedTime) && snapshotTime > savedTime) {
        return false;
      }
    }
  }

  return Date.now() <= publishTime + STALE_BUFFER_MS;
}

export function readPendingReviewState(
  userId: string | undefined,
  courseId: string,
  snapshotGeneratedAt?: string,
): PendingReviewState | null {
  if (!userId || !canUseStorage()) {
    return null;
  }

  const key = storageKey(userId, courseId);
  const stored = parseStoredValue<(StoredPendingReview & { term?: string })>(window.localStorage.getItem(key));
  if (!stored || !isPendingStillRelevant(stored.publishAt, stored.savedAt, snapshotGeneratedAt)) {
    window.localStorage.removeItem(key);
    return null;
  }

  return {
    publishAt: stored.publishAt,
    savedAt: stored.savedAt,
    professor: stored.professor,
    quarter: stored.quarter ?? stored.term ?? "",
    median: stored.median ?? null,
    comments: stored.comments,
  };
}

export function savePendingReviewState(
  userId: string | undefined,
  courseId: string,
  review: {
    publishAt: string;
    professor: string;
    quarter: string;
    median?: "A" | "A-" | "B+" | "B" | "B-" | "C+" | "Unsure" | null;
    comments: string;
  },
): PendingReviewState | null {
  if (!userId || !canUseStorage()) {
    return null;
  }

  const key = storageKey(userId, courseId);
  const nextState: PendingReviewState = {
    publishAt: review.publishAt,
    savedAt: new Date().toISOString(),
    professor: review.professor,
    quarter: review.quarter,
    median: review.median ?? null,
    comments: review.comments,
  };

  window.localStorage.setItem(key, JSON.stringify(nextState));
  return nextState;
}

export function clearPendingReviewState(userId: string | undefined, courseId: string): void {
  if (!userId || !canUseStorage()) {
    return;
  }
  window.localStorage.removeItem(storageKey(userId, courseId));
}
